import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from '../styles.module.scss';
import { ExtractionValueType, TYPE_OPTIONS, describeListHint } from './schemaModel';
import { EditorActions } from './editorTypes';
import PropertyKeyInput from './PropertyKeyInput';

interface FieldEditorCardProps {
    pathId: string;
    propertyKey: string;
    serializedObjectPath: string;
    serializedPropertyPath: string;
    title: string;
    description: string;
    valueType: ExtractionValueType;
    arrayDepth: number;
    required: boolean;
    displayName: string;
    actions: EditorActions;
}

export default function FieldEditorCard({
    pathId,
    propertyKey,
    serializedObjectPath,
    serializedPropertyPath,
    title,
    description,
    valueType,
    arrayDepth,
    required,
    displayName,
    actions,
}: FieldEditorCardProps) {
    const [draftTitle, setDraftTitle] = useState(title);
    const [draftDescription, setDraftDescription] = useState(description);
    const multiple = arrayDepth > 0;

    // Name and description are drafted locally and committed on blur so typing
    // does not re-render (and re-serialize) the whole schema per keystroke.
    const commitDrafts = () => {
        actions.commitFieldEdits(serializedObjectPath, propertyKey, {
            title: draftTitle,
            description: draftDescription,
        });
    };

    // Safety net: if the card closes without a blur (switching rows, switching
    // tabs), commit any pending draft. commitFieldEdits is idempotent, so the
    // usual blur-then-unmount sequence emits a single change.
    const commitArgsRef = useRef({ serializedObjectPath, propertyKey, draftTitle, draftDescription });
    commitArgsRef.current = { serializedObjectPath, propertyKey, draftTitle, draftDescription };
    const actionsRef = useRef(actions);
    actionsRef.current = actions;
    useEffect(
        () => () => {
            const args = commitArgsRef.current;
            actionsRef.current.commitFieldEdits(args.serializedObjectPath, args.propertyKey, {
                title: args.draftTitle,
                description: args.draftDescription,
            });
        },
        [],
    );

    return (
        <div className={styles.extractionSchemaEditorCard} data-test-id={`extraction-schema-editor-card-${pathId}`}>
            <div className={styles.extractionSchemaCardLine}>
                <label className={classNames(styles.extractionSchemaField, styles.extractionSchemaCardName)}>
                    <span>Field name</span>
                    <input
                        value={draftTitle}
                        placeholder="What should this field be called?"
                        autoFocus
                        onChange={(event) => setDraftTitle(event.target.value)}
                        onBlur={commitDrafts}
                        data-test-id={`extraction-schema-title-${pathId}`}
                    />
                </label>
                <div className={styles.extractionSchemaCardKey}>
                    <PropertyKeyInput
                        propertyKey={propertyKey}
                        pathId={pathId}
                        onRename={(nextKey) => actions.renameKey(serializedObjectPath, propertyKey, nextKey)}
                    />
                </div>
                <label className={classNames(styles.extractionSchemaField, styles.extractionSchemaCardType)}>
                    <span>Type</span>
                    <select
                        value={valueType}
                        onChange={(event) =>
                            actions.changeFieldShape(
                                serializedPropertyPath,
                                displayName,
                                event.target.value as ExtractionValueType,
                                multiple,
                            )
                        }
                        data-test-id={`extraction-schema-type-${pathId}`}
                    >
                        {TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>
                <label className={styles.extractionSchemaListOption} title={describeListHint(multiple, arrayDepth)}>
                    <input
                        type="checkbox"
                        checked={multiple}
                        onChange={() =>
                            actions.changeFieldShape(serializedPropertyPath, displayName, valueType, !multiple)
                        }
                        data-test-id={`extraction-schema-list-${pathId}`}
                    />
                    <span>
                        Capture as a list
                        {arrayDepth > 1 ? <small>{arrayDepth} levels</small> : null}
                    </span>
                </label>
                <label
                    className={styles.extractionSchemaListOption}
                    title="Mark this field as required in the extraction schema."
                >
                    <input
                        type="checkbox"
                        checked={required}
                        onChange={(event) =>
                            actions.toggleRequired(serializedObjectPath, propertyKey, event.target.checked)
                        }
                        data-test-id={`extraction-schema-required-${pathId}`}
                    />
                    <span>Required</span>
                </label>
            </div>
            <label className={styles.extractionSchemaField}>
                <span>Instructions</span>
                <textarea
                    value={draftDescription}
                    placeholder="Add helpful details about what should be captured from the document"
                    rows={2}
                    onChange={(event) => setDraftDescription(event.target.value)}
                    onBlur={commitDrafts}
                    data-test-id={`extraction-schema-description-${pathId}`}
                />
            </label>
            <div className={styles.extractionSchemaCardFooter}>
                <button
                    type="button"
                    className={styles.extractionSchemaDoneLink}
                    onClick={() => {
                        commitDrafts();
                        actions.closeEditor();
                    }}
                    data-test-id={`extraction-schema-done-${pathId}`}
                >
                    Done
                </button>
            </div>
        </div>
    );
}
